/**
 * ServiceContractSignPage — Batch #14.240
 *
 * Page PUBLIQUE (magic link, aucune connexion) : /contrat-service/:token
 * Le client consulte le contrat, coche les cases prévues (s'il y en a),
 * puis signe — la date est posée automatiquement.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileSignature, CheckCircle2, Loader2, Download } from "lucide-react";
import { toast } from "@/lib/toast";
import { SignatureCanvas } from "@/pages/public/FiscalMandatePage";
import PdfFrame from "@/components/shared/PdfFrame";
import { API } from "@/api";

const NAVY = "#002a80";

export default function ServiceContractSignPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [info, setInfo] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [signature, setSignature] = useState("");
  const [hasSignature, setHasSignature] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/service-contract/${token}`);
        setInfo(r.data);
        setBoxes((r.data.checkboxes || []).map(() => false));
        if (r.data.status === "completed") setDone(true);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const pdfUrl = `${API}/public/service-contract/${token}/pdf`;

  const sign = async () => {
    if (!hasSignature) { toast.error("Veuillez signer dans le cadre"); return; }
    if (!consent) { toast.error("Veuillez accepter les termes du contrat"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/service-contract/${token}/sign`, {
        signature_image_b64: signature,
        consent: true,
        checkbox_values: boxes,
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la signature — réessayez");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center max-w-md" data-testid="contract-not-found">
          <FileSignature className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <h1 className="text-lg font-bold text-slate-800 mb-1">Lien invalide</h1>
          <p className="text-sm text-slate-500">Ce lien n'existe pas ou n'est plus disponible. Contactez DG Comptabilité au 418 376-4298.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="service-contract-sign-page">
      <div className="text-white py-6 px-4" style={{ background: NAVY }}>
        <div className="max-w-3xl mx-auto">
          <h1 className="text-xl sm:text-2xl font-bold">DG Comptabilité</h1>
          <p className="text-sm opacity-80 mt-0.5">{info.title} — {info.client_name}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 mt-6 space-y-4">
        {done ? (
          <div className="bg-white rounded-2xl border-2 border-emerald-200 p-8 text-center" data-testid="contract-done">
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500 mb-3" />
            <h2 className="text-xl font-bold text-slate-800 mb-1">Merci !</h2>
            <p className="text-sm text-slate-600 mb-4">
              Votre contrat a bien été signé avec la date du jour.
            </p>
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-bold"
              style={{ background: NAVY }} data-testid="contract-download-signed">
              <Download className="w-4 h-4" /> Télécharger mon contrat signé
            </a>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-600">
              Bonjour {info.client_name} 👋 — voici votre contrat de services. Lisez-le
              attentivement, cochez vos choix s'il y a lieu, puis signez.
            </p>
            {info.notes && <p className="text-sm text-slate-500 italic bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">« {info.notes} »</p>}

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-2 border-b flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600">Votre contrat</span>
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                  className="text-xs underline" style={{ color: NAVY }} data-testid="contract-open-pdf">
                  Ouvrir / télécharger le PDF
                </a>
              </div>
              <PdfFrame title="Contrat de services" src={pdfUrl} height="70vh" accent={NAVY} label="Ouvrir mon contrat (PDF)" testId="contract-iframe" />
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
              <h3 className="text-sm font-bold text-slate-800">Acceptation et signature</h3>

              {(info.checkboxes || []).length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-600">Vos choix :</p>
                  {info.checkboxes.map((label, i) => (
                    <label key={i} className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer bg-blue-50/50 border border-blue-200 rounded-xl px-3 py-2.5">
                      <input type="checkbox" checked={boxes[i] || false}
                        onChange={(e) => setBoxes(boxes.map((b, j) => j === i ? e.target.checked : b))}
                        className="w-4 h-4 mt-0.5 accent-blue-900" data-testid={`contract-checkbox-${i}`} />
                      <span className="font-medium">{label}</span>
                    </label>
                  ))}
                </div>
              )}

              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  className="w-4 h-4 mt-0.5 accent-blue-900" data-testid="contract-consent" />
                <span>J'ai lu le contrat et j'accepte les termes et conditions décrits ci-dessus.</span>
              </label>
              <SignatureCanvas onChange={(dataUrl, has) => { setSignature(dataUrl); setHasSignature(has); }} />
              <p className="text-[11px] text-slate-400">
                La date du jour sera inscrite automatiquement sur le contrat au moment de la signature.
              </p>
              <button onClick={sign} disabled={submitting || !hasSignature || !consent}
                className="w-full sm:w-auto px-6 py-3 rounded-xl text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40"
                style={{ background: NAVY }} data-testid="contract-sign-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                {submitting ? "Signature en cours..." : "Signer le contrat"}
              </button>
            </div>
          </>
        )}
        <p className="text-[11px] text-slate-400 text-center pt-4">
          418 376-4298 · d.gougeon@outlook.fr · 412, rue J-R. Théberge, Chicoutimi, QC, G7J 3R9
        </p>
      </div>
    </div>
  );
}
